import { Injectable } from '@angular/core';
import { EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX, NumberUtils, SECOND_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import { isAccount, isFli, isGroup, isSubfli } from '@modeso/aumico-lib-templates-fe';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    AccountType,
    FixedAssetValueChange,
    FixedAssetsSchedule,
    FixedAssetsScheduleMovement,
    FixedAssetsScheduleReferenceType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { IFasAccountValue } from '../../interfaces/fas-account-value.interface';
import { FixedAssetFormValueChange } from '../../interfaces/fixed-asset-form-value-change.interface';
import { FixedAssetsScheduleFormData } from '../../interfaces/fixed-assets-schedule-form-data.interface';
import { FixedAssetsScheduleFormMovement } from '../../interfaces/fixed-assets-schedule-form-movement.interface';
import { IFixedAssetsScheduleReference } from '../../interfaces/fixed-assets-schedule-reference.interface';
export const DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME = 'DIRECT_ASSIGNED_GROUPED_MOVEMENTS';
@Injectable()
export class FixedAssetsScheduleService {
    constructor(private localizedTexts: I18NextService) {}

    getReferenceOptions(mappedTemplate: IAccountMappingTemplate): IFixedAssetsScheduleReference[] {
        const referenceOptions: IFixedAssetsScheduleReference[] = [];

        if (mappedTemplate) {
            const templateGroups: IAccountMappingTemplateGroup[] = mappedTemplate.group.groups;
            referenceOptions.push(...this.getGroupAndFliReferences(templateGroups));
        }

        return referenceOptions;
    }

    private getGroupAndFliReferences(templateGroups: IAccountMappingTemplateGroup[]): IFixedAssetsScheduleReference[] {
        const groupReferences: IFixedAssetsScheduleReference[] = [];
        for (const group of templateGroups) {
            if (group.isHeader || group.isEmpty) {
                continue;
            }

            const groupReference: IFixedAssetsScheduleReference = {
                reference: group.name,
                referenceCaption: group.title ?? EMPTY_STRING,
                referenceType: FixedAssetsScheduleReferenceType.GROUP,
            };
            groupReferences.push(groupReference);

            const fliReferences: IFixedAssetsScheduleReference[] = this.getFliReferencesOfGroups(group.items);

            groupReferences.push(...fliReferences);

            if (group.groups && group.groups.length > 0) {
                groupReferences.push(...this.getGroupAndFliReferences(group.groups));
            }
        }
        return groupReferences;
    }

    private getFliReferencesOfGroups(groupItems: AccountMappingTemplateGroupItem[]): IFixedAssetsScheduleReference[] {
        const fliReferences: IFixedAssetsScheduleReference[] = [];
        for (const item of groupItems) {
            if (item.type === ItemType.SUB_TOTAL || item.type === ItemType.EMPTY || item.type === ItemType.HEADER) {
                // skip subtotals as they have no nested items inside them
                continue;
            }

            const fliReference: IFixedAssetsScheduleReference = {
                reference: item.name,
                referenceCaption: item.caption ?? EMPTY_STRING,
                referenceType: FixedAssetsScheduleReferenceType.FLI,
            };
            fliReferences.push(fliReference);
        }
        return fliReferences;
    }

    getInitialFixedAssetsScheduleFormData(
        mappedTemplate: IAccountMappingTemplate,
        reference: IFixedAssetsScheduleReference,
        locale: string,
    ): FixedAssetsScheduleFormData {
        const templateGroups: IAccountMappingTemplateGroup[] = [mappedTemplate.group];
        return {
            reference,
            costsLabel: this.localizedTexts.t('aumico_annual_report_fas_section_costs_title', {
                lng: locale,
            }) as string,
            depreciationLabel: this.localizedTexts.t('aumico_annual_report_fas_section_depr_title', {
                lng: locale,
            }) as string,
            valueAppreciationLabel: this.localizedTexts.t('aumico_annual_report_fas_section_valAppr_title', {
                lng: locale,
            }) as string,
            movements: this.getMovements(reference, templateGroups),
        };
    }

    getSavedFixedAssetsScheduleFormData(
        mappedTemplate: IAccountMappingTemplate,
        reference: IFixedAssetsScheduleReference,
        fixedAssetsSchedule: FixedAssetsSchedule,
    ): FixedAssetsScheduleFormData {
        const templateGroups: IAccountMappingTemplateGroup[] = [mappedTemplate.group];
        return {
            reference,
            costsLabel: fixedAssetsSchedule.costsLabel,
            depreciationLabel: fixedAssetsSchedule.depreciationsLabel,
            valueAppreciationLabel: fixedAssetsSchedule.valueAppreciationLabel,
            movements: this.getMovements(reference, templateGroups, fixedAssetsSchedule),
        };
    }

    getAutofilledFixedAssetsScheduleFormDataFromPreviousYear(
        mappedTemplate: IAccountMappingTemplate,
        reference: IFixedAssetsScheduleReference,
        previousYearFixedAssetsSchedule: FixedAssetsSchedule,
    ): FixedAssetsScheduleFormData {
        const templateGroups: IAccountMappingTemplateGroup[] = [mappedTemplate.group];
        return {
            reference,
            costsLabel: previousYearFixedAssetsSchedule.costsLabel,
            depreciationLabel: previousYearFixedAssetsSchedule.depreciationsLabel,
            valueAppreciationLabel: previousYearFixedAssetsSchedule.valueAppreciationLabel,
            movements: this.getMovements(reference, templateGroups, undefined, previousYearFixedAssetsSchedule),
        };
    }

    getMovements(
        reference: IFixedAssetsScheduleReference,
        templateGroups: IAccountMappingTemplateGroup[],
        fixedAssetsSchedule?: FixedAssetsSchedule,
        previousYearFixedAssetsSchedule?: FixedAssetsSchedule,
    ): FixedAssetsScheduleFormMovement[] {
        // CASE: User chooses Group
        if (reference.referenceType === FixedAssetsScheduleReferenceType.GROUP) {
            const movements: FixedAssetsScheduleFormMovement[] = [];
            const foundGroup = this.searchForReferenceGroup(reference, templateGroups);

            if (foundGroup == null) {
                throw new Error('no matching group found for reference group');
            }

            if (foundGroup.sortedItems == null) {
                throw new Error('sortedItems array does not exist ');
            }

            if (foundGroup.sortedItems != null && foundGroup.sortedItems.length !== EMPTY_ARRAY_LENGTH) {
                if (fixedAssetsSchedule != null) {
                    return this.getSavedOrEmptyMovementsBasedOnSortedItems(
                        foundGroup.sortedItems as SortedMappingItem[],
                        0,
                        fixedAssetsSchedule,
                    );
                }

                if (previousYearFixedAssetsSchedule != null) {
                    return this.getAutofilledMovementsBasedOnSortedItems(
                        foundGroup.sortedItems as SortedMappingItem[],
                        0,
                        previousYearFixedAssetsSchedule,
                    );
                }

                return this.getSavedOrEmptyMovementsBasedOnSortedItems(
                    foundGroup.sortedItems as SortedMappingItem[],
                    0,
                    fixedAssetsSchedule,
                );
            }

            return this.cleanUpLevelsToStartAt0(movements);
        }
        // CASE: User chooses FLI
        if (reference.referenceType === FixedAssetsScheduleReferenceType.FLI) {
            const foundFli = this.searchForReferenceFli(reference, templateGroups);
            if (foundFli == null) {
                throw new Error('no matching fli found for reference fli');
            }

            let movements: FixedAssetsScheduleFormMovement[] = [];

            if (fixedAssetsSchedule != null) {
                movements = this.getMovementsValuesInsideFli(foundFli.accounts, 0, fixedAssetsSchedule);
            } else if (previousYearFixedAssetsSchedule != null) {
                movements = this.getMovementsValuesInsideFli(
                    foundFli.accounts,
                    0,
                    previousYearFixedAssetsSchedule,
                    true,
                );
            } else {
                movements = this.getMovementsValuesInsideFli(foundFli.accounts, 0);
            }

            return this.cleanUpLevelsToStartAt0(movements);
        }

        // CASE: User chooses SUB FLI OR OF THAT ITEM
        if (
            reference.referenceType === FixedAssetsScheduleReferenceType.SUB_FLI ||
            reference.referenceType === FixedAssetsScheduleReferenceType.OF_THAT_ITEM
        ) {
            // current implementation is that there is nothing inside a sub fli or of-that-item other than accounts
            return [];
        }
        throw new Error('referenceType is invalid');
    }

    private cleanUpLevelsToStartAt0(movements: FixedAssetsScheduleFormMovement[]): FixedAssetsScheduleFormMovement[] {
        if (movements.length === EMPTY_ARRAY_LENGTH) {
            return movements;
        }
        // Find the minimum level value in the original array
        const minLevel = movements.reduce(
            (min, movement) => (movement.level < min ? movement.level : min),
            movements[0].level,
        );

        // Create a new array with adjusted levels
        const newMovements = movements.map((movement) => ({
            ...movement,
            level: movement.level - minLevel,
        }));

        return newMovements;
    }

    private searchForReferenceGroup(
        reference: IFixedAssetsScheduleReference,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroup | undefined {
        for (let group of templateGroups) {
            if (group.name === reference.reference) {
                return group;
            }

            let nestedGroup = this.searchForReferenceGroup(reference, group.groups);
            if (nestedGroup != null) {
                return nestedGroup;
            }
        }
        return undefined;
    }

    private searchForReferenceFli(
        reference: IFixedAssetsScheduleReference,
        templateGroups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroupItemFLI | undefined {
        for (let group of templateGroups) {
            // First, check the items at the current group level
            for (let item of group.items) {
                if (item.name === reference.reference && item.type !== ItemType.SUB_TOTAL) {
                    return item as IAccountMappingTemplateGroupItemFLI;
                }
            }

            // If not found at current level, search within nested groups
            let nestedItem = this.searchForReferenceFli(reference, group.groups);
            if (nestedItem) {
                return nestedItem;
            }
        }
        return null;
    }

    private createEmptyGroupedMovement(account: IAccount, level: number) {
        return {
            name: DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME,
            level: level,
            excludeFromCounting: true,
            caption: this.localizedTexts.t('aumico_annual_report_fas_direct_assigned_accounts') as string,
            openingValue: account.calculatedOpeningBalance,
            closingValue: account.calculatedClosingBalance,
            costs: this.getEmptyFixedAssetValueChangeForCostsAndDepreciation(),
            depreciations: this.getEmptyFixedAssetValueChangeForCostsAndDepreciation(),
            valueAppreciations: this.getEmptyFixedAssetValueChangeForValueAppreciation(),
        };
    }
    private createEmptyMovement(
        item: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        level: number,
    ): FixedAssetsScheduleFormMovement {
        return {
            name: item.name,
            level: level,
            caption: isGroup(item) ? item.title : item.caption,
            openingValue: item.calculatedOpeningBalance,
            closingValue: item.calculatedClosingBalance,
            costs: this.getEmptyFixedAssetValueChangeForCostsAndDepreciation(),
            depreciations: this.getEmptyFixedAssetValueChangeForCostsAndDepreciation(),
            valueAppreciations: this.getEmptyFixedAssetValueChangeForValueAppreciation(),
        };
    }

    private createValuedMovement(
        item: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        level: number,
        fixedAssetsScheduleMovement: FixedAssetsScheduleMovement,
        applyAutofill?: boolean,
    ): FixedAssetsScheduleFormMovement {
        return {
            name: item.name,
            level: level,
            caption: isGroup(item) ? item.title : item.caption,
            openingValue: item.calculatedOpeningBalance,
            closingValue: item.calculatedClosingBalance,
            ...this.assignValuesToCostsAndDepreciationsAndAppreciations(fixedAssetsScheduleMovement, applyAutofill),
        };
    }

    private createValuedGroupedMovement(
        account: IAccount,
        level: number,
        fixedAssetsScheduleMovement: FixedAssetsScheduleMovement,
        applyAutofill?: boolean,
    ): FixedAssetsScheduleFormMovement {
        return {
            name: DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME,
            level: level,
            excludeFromCounting: true,
            caption: this.localizedTexts.t('aumico_annual_report_fas_direct_assigned_accounts') as string,
            openingValue: account.calculatedOpeningBalance,
            closingValue: account.calculatedClosingBalance,
            ...this.assignValuesToCostsAndDepreciationsAndAppreciations(fixedAssetsScheduleMovement, applyAutofill),
        };
    }

    private assignValuesToCostsAndDepreciationsAndAppreciations(
        fixedAssetsScheduleMovement: FixedAssetsScheduleMovement,
        applyAutofill?: boolean,
    ): {
        costs: FixedAssetFormValueChange;
        depreciations?: FixedAssetFormValueChange;
        valueAppreciations?: FixedAssetFormValueChange;
    } {
        return {
            costs: applyAutofill
                ? this.getAutofilledValuesOfFixedAssetChangeForCostsAndDepreciations(fixedAssetsScheduleMovement.costs)
                : this.getSavedValuesOfFixedAssetChangeForCostsAndDepreciations(fixedAssetsScheduleMovement.costs),
            depreciations: applyAutofill
                ? this.getAutofilledValuesOfFixedAssetChangeForCostsAndDepreciations(
                      fixedAssetsScheduleMovement.depreciations,
                  )
                : this.getSavedValuesOfFixedAssetChangeForCostsAndDepreciations(
                      fixedAssetsScheduleMovement.depreciations,
                  ),
            valueAppreciations: applyAutofill
                ? this.getEmptyFixedAssetValueChangeForValueAppreciation()
                : this.getSavedValuesOfFixedAssetChangeForValueAppreciation(
                      fixedAssetsScheduleMovement.valueAppreciation,
                  ),
        };
    }

    private getMovementsValuesInsideFli(
        accounts: AccountMappingAccountItem[],
        level: number,
        fixedAssetsSchedule?: FixedAssetsSchedule,
        applyAutofill?: boolean,
    ) {
        const movements: FixedAssetsScheduleFormMovement[] = [];
        let groupedMovement: FixedAssetsScheduleFormMovement = undefined;
        const hasSubFLis: boolean =
            accounts.filter((account) => account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM)
                .length > 0;

        for (const account of accounts) {
            if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                const fixedAssetsScheduleMovement = fixedAssetsSchedule?.movements.find(
                    (movement) => movement.name === account.name,
                );
                //This check is here in case the saved values changed...
                if (fixedAssetsScheduleMovement != null) {
                    movements.push(
                        this.createValuedMovement(account, level + 1, fixedAssetsScheduleMovement, applyAutofill),
                    );
                } else {
                    movements.push(this.createEmptyMovement(account, level + 1));
                }
            } else if (account.type === AccountType.BALANCE || account.type === AccountType.INCOME) {
                if (!groupedMovement && hasSubFLis) {
                    const fixedAssetsScheduleMovement = fixedAssetsSchedule?.movements.find(
                        (movement) => movement.name === DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME,
                    );
                    if (fixedAssetsScheduleMovement != null) {
                        groupedMovement = this.createValuedGroupedMovement(
                            account,
                            level + 1,
                            fixedAssetsScheduleMovement,
                            applyAutofill,
                        );
                    } else {
                        groupedMovement = this.createEmptyGroupedMovement(account, level + 1);
                    }
                } else if (hasSubFLis) {
                    groupedMovement.openingValue += account.calculatedOpeningBalance;
                    groupedMovement.closingValue += account.calculatedClosingBalance;
                }
            }
        }
        if (groupedMovement) {
            return [groupedMovement, ...movements];
        }
        return movements;
    }

    private getSavedOrEmptyMovementsBasedOnSortedItems(
        sortedItems: SortedMappingItem[],
        level: number,
        fixedAssetsSchedule?: FixedAssetsSchedule,
    ): FixedAssetsScheduleFormMovement[] {
        const movements: FixedAssetsScheduleFormMovement[] = [];

        for (const sortedItem of sortedItems) {
            if (isGroup(sortedItem)) {
                const group: IAccountMappingTemplateGroup = cloneDeep<IAccountMappingTemplateGroup>(sortedItem);

                if (!sortedItem.isHiddenGroup) {
                    if (fixedAssetsSchedule == null) {
                        movements.push(this.createEmptyMovement(group, level));
                    } else {
                        const fixedAssetsScheduleMovement = fixedAssetsSchedule?.movements.find(
                            (movement) => movement.name === group.name,
                        );
                        if (fixedAssetsScheduleMovement == null) {
                            movements.push(this.createEmptyMovement(group, level));
                        } else {
                            movements.push(this.createValuedMovement(group, level, fixedAssetsScheduleMovement));
                        }
                    }
                }

                if (group.sortedItems.length != EMPTY_ARRAY_LENGTH) {
                    movements.push(
                        ...this.getSavedOrEmptyMovementsBasedOnSortedItems(
                            group.sortedItems as SortedMappingItem[],
                            level + 1,
                            fixedAssetsSchedule,
                        ),
                    );
                }
            } else {
                const item: AccountMappingTemplateGroupItem = cloneDeep<AccountMappingTemplateGroupItem>(sortedItem);
                if (item.type === ItemType.FLI) {
                    const fixedAssetsScheduleMovements = fixedAssetsSchedule?.movements.filter(
                        (movement) => movement.name === item.name,
                    );
                    let fixedAssetsScheduleMovement: FixedAssetsScheduleMovement;
                    if (
                        fixedAssetsScheduleMovements == null ||
                        fixedAssetsScheduleMovements.length === EMPTY_ARRAY_LENGTH
                    ) {
                        movements.push(this.createEmptyMovement(item, level));
                    }

                    if (fixedAssetsScheduleMovements != null && fixedAssetsScheduleMovements.length == 1) {
                        fixedAssetsScheduleMovement = fixedAssetsScheduleMovements[FIRST_ELEMENT_INDEX];

                        movements.push(this.createValuedMovement(item, level, fixedAssetsScheduleMovement));
                    } else if (fixedAssetsScheduleMovements != null && fixedAssetsScheduleMovements.length > 1) {
                        // if fixedAssetsScheduleMovements includes more than one match, then this means that a group name has the same name as an fli
                        // skip first one since it would be of type 'group' and take the second one which would be of type 'fli'
                        // Group names are unique so no group name would exist twice and no fli name would exist twice
                        fixedAssetsScheduleMovement = fixedAssetsScheduleMovements[SECOND_ELEMENT_INDEX];
                        movements.push(this.createValuedMovement(item, level, fixedAssetsScheduleMovement));
                    }

                    movements.push(...this.getMovementsValuesInsideFli(item.accounts, level, fixedAssetsSchedule));
                }
            }
        }

        return movements;
    }

    private getAutofilledMovementsBasedOnSortedItems(
        sortedItems: SortedMappingItem[],
        level: number,
        previousYearFixedAssetsSchedule: FixedAssetsSchedule,
    ): FixedAssetsScheduleFormMovement[] {
        const movements: FixedAssetsScheduleFormMovement[] = [];

        for (const sortedItem of sortedItems) {
            if (isGroup(sortedItem)) {
                const group: IAccountMappingTemplateGroup = cloneDeep<IAccountMappingTemplateGroup>(sortedItem);

                if (!sortedItem.isHiddenGroup) {
                    const fixedAssetsScheduleMovement = previousYearFixedAssetsSchedule.movements.find(
                        (movement) => movement.name === group.name,
                    );
                    if (fixedAssetsScheduleMovement == null) {
                        // if mapping item is not found in previous year fas, then we add empty values for it

                        movements.push(this.createEmptyMovement(group, level));
                    } else {
                        movements.push(this.createValuedMovement(group, level, fixedAssetsScheduleMovement, true));
                    }
                }

                if (group.sortedItems.length != EMPTY_ARRAY_LENGTH) {
                    movements.push(
                        ...this.getAutofilledMovementsBasedOnSortedItems(
                            group.sortedItems as SortedMappingItem[],
                            level + 1,
                            previousYearFixedAssetsSchedule,
                        ),
                    );
                }
            } else {
                const item: AccountMappingTemplateGroupItem = cloneDeep<AccountMappingTemplateGroupItem>(sortedItem);
                if (item.type === ItemType.FLI) {
                    const fixedAssetsScheduleMovements = previousYearFixedAssetsSchedule.movements.filter(
                        (movement) => movement.name === item.name,
                    );

                    let fixedAssetsScheduleMovement: FixedAssetsScheduleMovement;
                    if (fixedAssetsScheduleMovements.length === EMPTY_ARRAY_LENGTH) {
                        // if mapping item is not found in previous fas, then we add it with empty values
                        movements.push(this.createEmptyMovement(item, level));
                    }

                    if (fixedAssetsScheduleMovements.length == 1) {
                        fixedAssetsScheduleMovement = fixedAssetsScheduleMovements[FIRST_ELEMENT_INDEX];

                        movements.push(this.createValuedMovement(item, level, fixedAssetsScheduleMovement, true));
                    } else if (fixedAssetsScheduleMovements.length > 1) {
                        // if fixedAssetsScheduleMovements includes more than one match, then this means that a group name has the same name as an fli
                        // skip first one since it would be of type 'group' and take the second one which would be of type 'fli'
                        // Group names are unique so no group name would exist twice and no fli name would exist twice
                        fixedAssetsScheduleMovement = fixedAssetsScheduleMovements[SECOND_ELEMENT_INDEX];

                        movements.push(this.createValuedMovement(item, level, fixedAssetsScheduleMovement, true));
                    }

                    movements.push(
                        ...this.getMovementsValuesInsideFli(
                            item.accounts,
                            level,
                            previousYearFixedAssetsSchedule,
                            true,
                        ),
                    );
                }
            }
        }

        return movements;
    }

    private getEmptyFixedAssetValueChangeForCostsAndDepreciation(): FixedAssetFormValueChange {
        return {
            openingValue: 0,
            additions: null,
            disposals: null,
            transfers: null,
            closingValue: 0,
            otherValue: 0,
            isValid: true,
        };
    }

    private getEmptyFixedAssetValueChangeForValueAppreciation(): FixedAssetFormValueChange {
        return {
            openingValue: 0,
            otherValue: 0,
            isValid: true,
        };
    }

    private getSavedValuesOfFixedAssetChangeForCostsAndDepreciations(
        fixedAssetValueChange: FixedAssetValueChange,
    ): FixedAssetFormValueChange {
        return {
            ...fixedAssetValueChange,
            otherValue: 0,
            isValid: true,
        };
    }

    private getSavedValuesOfFixedAssetChangeForValueAppreciation(
        valueAppreciation: number | null,
    ): FixedAssetFormValueChange {
        return {
            openingValue: valueAppreciation,
            otherValue: 0,
            isValid: true,
        };
    }

    private getAutofilledValuesOfFixedAssetChangeForCostsAndDepreciations(
        previousFixedAssetValueChange: FixedAssetValueChange,
    ): FixedAssetFormValueChange {
        return {
            openingValue: previousFixedAssetValueChange.closingValue,
            additions: null,
            disposals: null,
            transfers: null,
            closingValue: 0,
            otherValue: 0,
            isValid: true,
        };
    }

    canApplyAutofill(
        selectedReference: IFixedAssetsScheduleReference,
        previousYearReferenceName: string,
        previousYearReferenceType: FixedAssetsScheduleReferenceType,
    ): boolean {
        return (
            selectedReference.reference === previousYearReferenceName &&
            selectedReference.referenceType === previousYearReferenceType
        );
    }

    fillMovementsFromUploadedFile(
        fixedAssetsScheduleMovements: FixedAssetsScheduleFormMovement[],
        selectedReference: IFixedAssetsScheduleReference,
        mappedTemplate: IAccountMappingTemplate,
        fasAccountsValues: IFasAccountValue[],
    ): FixedAssetsScheduleFormMovement[] {
        const filledFixedAssetsScheduleMovements: FixedAssetsScheduleFormMovement[] =
            cloneDeep<FixedAssetsScheduleFormMovement[]>(fixedAssetsScheduleMovements);

        const templateGroups: IAccountMappingTemplateGroup[] = [mappedTemplate.group];

        if (selectedReference.referenceType === FixedAssetsScheduleReferenceType.GROUP) {
            const foundGroup = this.searchForReferenceGroup(selectedReference, templateGroups);

            if (foundGroup == null) {
                throw new Error('no matching group found for reference group');
            }

            if (foundGroup.sortedItems == null) {
                throw new Error('sortedItems array does not exist ');
            }

            this.fillMovementsInsideGroup(foundGroup, filledFixedAssetsScheduleMovements, fasAccountsValues);
        }

        if (selectedReference.referenceType === FixedAssetsScheduleReferenceType.FLI) {
            const foundFli = this.searchForReferenceFli(selectedReference, templateGroups);

            if (foundFli == null) {
                throw new Error('no matching fli found for reference fli');
            }

            const fliSubflis = foundFli.accounts.filter((acc) => isSubfli(acc));

            if (fliSubflis.length !== EMPTY_ARRAY_LENGTH) {
                this.fillMovementsInsideFli(foundFli, filledFixedAssetsScheduleMovements, fasAccountsValues);
            }
        }

        return filledFixedAssetsScheduleMovements;
    }

    private fillMovementsInsideGroup(
        foundGroup: IAccountMappingTemplateGroup,
        fixedAssetsScheduleMovements: FixedAssetsScheduleFormMovement[],
        fasAccountsValues: IFasAccountValue[],
    ): void {
        if (foundGroup.sortedItems != null && foundGroup.sortedItems.length !== EMPTY_ARRAY_LENGTH) {
            for (const sortedItem of foundGroup.sortedItems as SortedMappingItem[]) {
                if (isGroup(sortedItem)) {
                    this.fillMovementsInsideGroup(sortedItem, fixedAssetsScheduleMovements, fasAccountsValues);
                }

                if (isFli(sortedItem)) {
                    this.fillMovementsInsideFli(sortedItem, fixedAssetsScheduleMovements, fasAccountsValues);
                }
            }
        }
    }

    private fillMovementsInsideFli(
        foundFli: IAccountMappingTemplateGroupItemFLI,
        fixedAssetsScheduleMovements: FixedAssetsScheduleFormMovement[],
        fasAccountsValues: IFasAccountValue[],
    ): void {
        const fliAccounts = foundFli.accounts.filter((acc) => isAccount(acc));
        const fliSubflis = foundFli.accounts.filter((acc) => isSubfli(acc));

        if (fliSubflis.length > EMPTY_ARRAY_LENGTH) {
            // check for direct assigned accounts
            const correspondingFliMovementIndex = fixedAssetsScheduleMovements.findIndex(
                (move) => move.name === foundFli.name,
            );

            const nextDirectAssignedAccountsMovement = fixedAssetsScheduleMovements[correspondingFliMovementIndex + 1];
            if (
                fliAccounts.length > EMPTY_ARRAY_LENGTH &&
                nextDirectAssignedAccountsMovement.name === DIRECT_ASSIGNED_ACCOUNTS_MOVEMENT_NAME
            ) {
                const { costs, depreciation, valueAppreciation } = this.getMovementValuesFromUploadedFile(
                    fliAccounts as IAccount[],
                    fasAccountsValues,
                );

                this.assignValuesToMovement(nextDirectAssignedAccountsMovement, costs, depreciation, valueAppreciation);
            }

            // accounts inside subfli
            for (const subfli of fliSubflis) {
                const correspondingMovement = fixedAssetsScheduleMovements.find((move) => move.name === subfli.name);

                const { costs, depreciation, valueAppreciation } = this.getMovementValuesFromUploadedFile(
                    (subfli as IAccountMappingSubFLI).accounts as IAccount[],
                    fasAccountsValues,
                );

                this.assignValuesToMovement(correspondingMovement, costs, depreciation, valueAppreciation);
            }
        } else {
            const correspondingMovement = fixedAssetsScheduleMovements.find((move) => move.name === foundFli.name);

            const { costs, depreciation, valueAppreciation } = this.getMovementValuesFromUploadedFile(
                fliAccounts as IAccount[],
                fasAccountsValues,
            );

            this.assignValuesToMovement(correspondingMovement, costs, depreciation, valueAppreciation);
        }
    }

    private getMovementValuesFromUploadedFile(
        fliAccounts: IAccount[],
        fasAccountsValues: IFasAccountValue[],
    ): {
        costs: FixedAssetValueChange;
        depreciation: FixedAssetValueChange;
        valueAppreciation: FixedAssetValueChange;
    } {
        let costsOpeningSummation = 0;
        let costsAdditionsSummation = 0;
        let costsDisposalsSummation = 0;
        let costsTransfersSummation = 0;
        let costsClosingSummation = 0;
        let depreciationOpeningSummation = 0;
        let depreciationAdditionsSummation = 0;
        let depreciationDisposalsSummation = 0;
        let depreciationTransfersSummation = 0;
        let depreciationClosingSummation = 0;
        let valueAppreciationOpeningSummation = 0;

        for (const fasAccountValue of fasAccountsValues) {
            if (
                fliAccounts.find(
                    (acc) =>
                        acc.accountNumber === fasAccountValue.accountNumber && acc.name === fasAccountValue.accountName,
                )
            ) {
                costsOpeningSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.costs.openingValue, costsOpeningSummation],
                    0,
                );

                costsAdditionsSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.costs.additions, costsAdditionsSummation],
                    0,
                );

                costsDisposalsSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.costs.disposals, costsDisposalsSummation],
                    0,
                );

                costsTransfersSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.costs.transfers, costsTransfersSummation],
                    0,
                );

                costsClosingSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.costs.closingValue, costsClosingSummation],
                    0,
                );

                depreciationOpeningSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.depreciation.openingValue, depreciationOpeningSummation],
                    0,
                );

                depreciationAdditionsSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.depreciation.additions, depreciationAdditionsSummation],
                    0,
                );

                depreciationDisposalsSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.depreciation.disposals, depreciationDisposalsSummation],
                    0,
                );

                depreciationTransfersSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.depreciation.transfers, depreciationTransfersSummation],
                    0,
                );

                depreciationClosingSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.depreciation.closingValue, depreciationClosingSummation],
                    0,
                );

                valueAppreciationOpeningSummation = NumberUtils.sumIgnoringInfiniteNumbers(
                    [fasAccountValue.valueAppreciation.openingValue, valueAppreciationOpeningSummation],
                    0,
                );
            }
        }

        return {
            costs: {
                openingValue: costsOpeningSummation,
                additions: costsAdditionsSummation,
                disposals: costsDisposalsSummation,
                transfers: costsTransfersSummation,
                closingValue: costsClosingSummation,
            },
            depreciation: {
                openingValue: depreciationOpeningSummation,
                additions: depreciationAdditionsSummation,
                disposals: depreciationDisposalsSummation,
                transfers: depreciationTransfersSummation,
                closingValue: depreciationClosingSummation,
            },
            valueAppreciation: {
                openingValue: valueAppreciationOpeningSummation,
                closingValue: 0,
            },
        };
    }

    private assignValuesToMovement(
        movement: FixedAssetsScheduleFormMovement,
        costs: FixedAssetValueChange,
        depreciation: FixedAssetValueChange,
        valueAppreciation: FixedAssetValueChange,
    ): void {
        movement.costs = {
            ...costs,
            isValid: movement.costs.isValid,
            closingValue: movement.costs.closingValue, // taking already calculated closing value and not from excel
            otherValue: movement.costs.otherValue,
        };

        movement.depreciations = {
            ...depreciation,
            isValid: movement.depreciations.isValid,
            closingValue: movement.depreciations.closingValue, // taking already calculated closing value and not from excel
            otherValue: movement.depreciations.otherValue,
        };

        movement.valueAppreciations = {
            ...valueAppreciation,
            isValid: movement.valueAppreciations.isValid,
            otherValue: movement.valueAppreciations.otherValue,
        };
    }
}
